import axios from "axios";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { mockFlags } from "@/lib/mock-flags";
import { env } from "@/lib/env";
import type { AuthSession, DeveloperUser } from "../api/types";

// Reexporta os tipos do usuário para quem consome o store (Topbar, etc.).
export type { DeveloperRole, DeveloperUser } from "../api/types";

interface AuthState {
  isAuthenticated: boolean;
  /**
   * Bootstrap concluído: o refresh silencioso de carregamento (`initAuth`) já
   * resolveu. Enquanto `false`, o layout aguarda (não redireciona para /login),
   * evitando o bounce que perderia a rota em deep-link/refresh.
   */
  bootstrapped: boolean;
  /** Access token — mantido só em memória (nunca persistido, ADR-0017). */
  accessToken: string | null;
  user: DeveloperUser | null;
  /** Abre a sessão a partir do resultado de login/social/callback. */
  setSession: (session: AuthSession) => void;
  logout: () => void;
}

const mockUser: DeveloperUser = {
  id: "dev-user-1",
  name: "Construtora Demo",
  email: "demo@construtora.com.br",
  role: "admin",
  developerId: "dev-1",
  developerName: "Incorporadora Demo",
  authProvider: "local",
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      // Em mock, já entra autenticado para o portal ser navegável sem backend.
      isAuthenticated: mockFlags.auth,
      // Mock não faz refresh; real só fica pronto após `initAuth`.
      bootstrapped: mockFlags.auth,
      accessToken: mockFlags.auth ? "mock-token" : null,
      user: mockFlags.auth ? mockUser : null,

      setSession: ({ accessToken, user }) =>
        set({ isAuthenticated: true, accessToken, user }),

      logout: () => set({ isAuthenticated: false, accessToken: null, user: null }),
    }),
    {
      name: "imobgo.launches.auth",
      // Nunca persistir tokens (ADR-0017). Só o perfil (UX) — o refresh token vive
      // no cookie HttpOnly `rt`, fora do alcance do JavaScript; o access token fica
      // só em memória e é reidratado via /auth/refresh no boot.
      partialize: (s) => ({ user: s.user }),
    },
  ),
);

/** Resposta de /auth/refresh (mesmo shape do login). */
interface RefreshResponse {
  accessToken: string;
  user: Omit<DeveloperUser, "authProvider">;
}

/**
 * Bootstrap de sessão no carregamento: em modo real, se há um perfil lembrado mas
 * nenhum access token em memória (reload), tenta um refresh silencioso — o cookie
 * HttpOnly `rt` autentica a renovação. Sem cookie válido, segue deslogado.
 *
 * Promessa única de bootstrap: o StrictMode (dev) e qualquer remontagem chamam
 * `initAuth` mais de uma vez. Sem dedupe, dispararíamos dois POST /auth/refresh
 * concorrentes — e como o refresh token é rotacionado no primeiro uso, o segundo
 * falharia (derrubando a sessão). Compartilhar a promessa garante um refresh só.
 */
let bootstrapPromise: Promise<void> | null = null;

async function runBootstrap(): Promise<void> {
  const { accessToken, user } = useAuthStore.getState();
  if (user && !accessToken) {
    try {
      // axios "cru" (sem os interceptors do apiClient) para não recursar no 401;
      // o cookie HttpOnly `rt` acompanha via withCredentials.
      const { data } = await axios.post<RefreshResponse>(
        `${env.VITE_API_BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true },
      );
      useAuthStore.getState().setSession({
        accessToken: data.accessToken,
        user: { ...data.user, authProvider: user.authProvider },
      });
    } catch {
      // Sem cookie válido: limpa o perfil lembrado e segue deslogado.
      useAuthStore.getState().logout();
    }
  }
  // Marca o fim do bootstrap (sucesso, falha ou nada a fazer) para liberar o layout.
  useAuthStore.setState({ bootstrapped: true });
}

export function initAuth(): Promise<void> {
  if (mockFlags.auth) return Promise.resolve(); // já entra bootstrapped: true
  bootstrapPromise ??= runBootstrap();
  return bootstrapPromise;
}
